/**
 * Funções de API para gerenciamento de Combos
 */

import { apiRequest } from '@/lib/api/client'
import type {
  Combo,
  ComboInput,
  ComboListResponse,
  ComboResponse,
  ComboDeleteResponse,
} from '@/types/combo'

/**
 * Lista todos os combos de uma campanha
 */
export async function fetchCombos(
  campaignId: number,
  activeOnly: boolean = false
): Promise<Combo[]> {
  const params = new URLSearchParams()
  if (activeOnly) {
    params.append('active_only', 'true')
  }
  const queryString = params.toString()
  const endpoint = `/api/campaigns/${campaignId}/combos${queryString ? `?${queryString}` : ''}`

  const response = await apiRequest<ComboListResponse>(endpoint, {
    method: 'GET',
  })
  return response.combos ?? []
}

/**
 * Busca um combo específico por ID
 */
export async function fetchComboById(comboId: number): Promise<Combo | null> {
  const response = await apiRequest<ComboResponse>(`/api/combos/${comboId}`, {
    method: 'GET',
  })
  return response.combo ?? null
}

/**
 * Cria um novo combo para uma campanha
 */
export async function createCombo(
  campaignId: number,
  data: ComboInput,
  token: string
): Promise<Combo | null> {
  const response = await apiRequest<ComboResponse>(
    `/api/campaigns/${campaignId}/combos`,
    {
      method: 'POST',
      body: JSON.stringify(data),
      token,
    }
  )
  return response.combo ?? null
}

/**
 * Atualiza um combo existente
 */
export async function updateCombo(
  comboId: number,
  data: Partial<ComboInput>,
  token: string
): Promise<Combo | null> {
  const response = await apiRequest<ComboResponse>(`/api/combos/${comboId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    token,
  })
  return response.combo ?? null
}

/**
 * Exclui um combo (soft delete - define is_active = false)
 */
export async function deleteCombo(
  comboId: number,
  token: string
): Promise<ComboDeleteResponse> {
  return apiRequest<ComboDeleteResponse>(`/api/combos/${comboId}`, {
    method: 'DELETE',
    token,
  })
}

/**
 * Formata preço para exibição em Real
 */
export function formatComboPrice(value: number | string): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(numValue)) return '-'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numValue)
}

/**
 * Calcula o desconto real baseado nos preços
 */
export function calculateDiscountPercent(fullPrice: number, comboPrice: number): number {
  if (fullPrice <= 0) return 0
  return Math.round(((fullPrice - comboPrice) / fullPrice) * 100)
}

function normalizeCampaignUnitPrice(price: number | string | null | undefined): number {
  if (typeof price === 'number') {
    return Number.isFinite(price) ? price : 0
  }

  if (typeof price === 'string') {
    const cleaned = price.replace(',', '.').trim()
    const parsed = Number.parseFloat(cleaned)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

export function calculateComboFullPriceFromCampaignPrice(
  campaignPrice: number | string | null | undefined,
  quantity: number,
  fullPriceFromApi: number
): number {
  const pricePerNumber = normalizeCampaignUnitPrice(campaignPrice)
  const validQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 0

  if (pricePerNumber > 0 && validQuantity > 0) {
    return pricePerNumber * validQuantity
  }

  return fullPriceFromApi
}

